import * as React from 'react'
import {
  Calendar as AriaCalendar,
  CalendarGrid,
  CalendarGridHeader,
  CalendarGridBody,
  CalendarHeaderCell,
  CalendarCell,
  Heading,
  Button as AriaButton,
  type CalendarProps as AriaCalendarProps,
  type DateValue,
} from 'react-aria-components'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { CalendarMonthYearSelect } from './calendar-month-year-select'

/**
 * Calendar — an accessible month grid for picking a single date, built on
 * react-aria-components (full keyboard nav, screen-reader announcements,
 * min/max + unavailable dates). Composed into one component: header with
 * month nav + the day grid.
 *
 *   <Calendar value={date} onChange={setDate} />
 *
 * Borderless by default — wrap it in a card for standalone use, or drop it in a
 * `Popover` (that's what `DatePicker` does). Tokens: selected day = basil-600
 * fill (the brand "chosen" treatment, matching the rest of the DS), today gets a
 * subtle ring, hover = gray-100, disabled/unavailable = gray-300. Phosphor
 * carets for nav. Boolean props map to react-aria's `is*`.
 */

const navButtonClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-700 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand data-[disabled]:text-fbui-gray-300 data-[disabled]:cursor-not-allowed'

const cellClass =
  'flex size-9 cursor-pointer items-center justify-center rounded-lg text-sm text-fbui-gray-700 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[focus-visible]:ring-2 data-[focus-visible]:ring-fbui-ring-brand data-[selected]:bg-fbui-basil-600 data-[selected]:text-fbui-base-white data-[selected]:data-[hovered]:bg-fbui-basil-700 data-[disabled]:cursor-default data-[disabled]:text-fbui-gray-300 data-[disabled]:data-[hovered]:bg-transparent data-[unavailable]:cursor-default data-[unavailable]:text-fbui-gray-300 data-[unavailable]:line-through data-[outside-month]:text-fbui-gray-300'

export interface CalendarProps extends Omit<
  AriaCalendarProps<DateValue>,
  'isDisabled' | 'isReadOnly' | 'className'
> {
  disabled?: boolean
  readOnly?: boolean
  /** Show month + year dropdowns in the header instead of a static label. */
  monthYearSelect?: boolean
  className?: string
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  ({ disabled, readOnly, monthYearSelect, className, ...props }, ref) => (
    <AriaCalendar
      ref={ref}
      isDisabled={disabled}
      isReadOnly={readOnly}
      className={cn('w-fit', className)}
      {...props}
    >
      <header className="mb-2 flex items-center justify-between gap-2">
        <AriaButton
          slot="previous"
          aria-label="Previous month"
          className={navButtonClass}
        >
          <CaretLeft weight="bold" className="size-4" aria-hidden="true" />
        </AriaButton>
        {monthYearSelect ? (
          <CalendarMonthYearSelect />
        ) : (
          <Heading className="text-sm font-semibold text-fbui-text-title" />
        )}
        <AriaButton
          slot="next"
          aria-label="Next month"
          className={navButtonClass}
        >
          <CaretRight weight="bold" className="size-4" aria-hidden="true" />
        </AriaButton>
      </header>
      <CalendarGrid weekdayStyle="short" className="border-collapse">
        <CalendarGridHeader>
          {(day) => (
            <CalendarHeaderCell className="size-9 text-xs font-normal text-fbui-gray-400">
              {day}
            </CalendarHeaderCell>
          )}
        </CalendarGridHeader>
        <CalendarGridBody>
          {(date) => <CalendarCell date={date} className={cellClass} />}
        </CalendarGridBody>
      </CalendarGrid>
    </AriaCalendar>
  )
)
Calendar.displayName = 'Calendar'
